import React from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsExporting from 'highcharts/modules/exporting'
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting'
import {
  GroupByOption,
  ValueColumnOption,
  AggregationMethodOption
} from './chart-options'
import { drillDownPlotOptions } from './drilldown'

HighchartsExporting(Highcharts)
HighchartsOfflineExporting(Highcharts)


function PieDonutChart({
  data,
  innerSize = 0,
  showLabels = true,
  title = '',
  showLegend = false,
  tooltipFormatter,
  colors = null,
  chartRef = null,
  onPointClick = null
}) {
  const seriesData = data.map((d) => ({
    name: (d.key && d.key !== 'undefined') ? d.key : 'None',
    y: parseFloat(d.value) || 0
  }))

  const options = {
    ...(colors && { colors }), // Apply brand colors if provided
    chart: {
      type: 'pie',
    },
    title: { text: title },
    legend: { enabled: showLegend },
    tooltip: { formatter: tooltipFormatter },
    plotOptions: {
      ...drillDownPlotOptions(onPointClick),
      pie: {
        innerSize: innerSize,
        dataLabels: {
          enabled: showLabels,
        },
      },
    },
    series: [
      {
        type: 'pie',
        name: title,
        colorByPoint: true,
        data: seriesData,
      },
    ],
    credits: { enabled: false },
    exporting: {
      enabled: true,
      filename: title || 'chart',
      fallbackToExportServer: false,
      libURL: '', // Prevent loading any external libraries
      buttons: {
        contextButton: {
          enabled: false
        }
      }
    },
  }

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} updateArgs={[true, true, false]} />
}

PieDonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    })
  ).isRequired,
  innerSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showLabels: PropTypes.bool,
  title: PropTypes.string,
  showLegend: PropTypes.bool,
  tooltipFormatter: PropTypes.func,
  colors: PropTypes.arrayOf(PropTypes.string),
  chartRef: PropTypes.object,
  onPointClick: PropTypes.func
}

// Chart configuration for pie/donut charts
export const pieDonutChartConfig = {
  requiredFields: ['title', 'groupBy'],

  validate: (formData) => {
    const errors = {}
    if (!formData.groupBy) {
      errors.groupBy = 'Group By column is required for pie charts'
    }
    return errors
  },

  isQueryEnabled: (formData, hasChartId) => {
    return !!(hasChartId && formData.groupBy)
  },

  buildChartParams: (formData) => ({
    group_by_column_id: formData.groupBy,
    slice_column_id: formData.valueColumn,
    slice_aggregation: formData.aggregationMethod
  }),

  // Build query params for API request
  buildQueryParams: (baseParams, formData, columns) => {
    const groupByCol = columns?.find((c) => c.id === formData.groupBy)
    const params = { ...baseParams }
    params.group_by = groupByCol?.database_column_name
    if (formData.valueColumn) {
      const valueCol = columns?.find((c) => c.id === formData.valueColumn)
      params.slice_column = valueCol?.database_column_name
      params.slice_aggregation = formData.aggregationMethod || 'sum'
    }
    return params
  },

  // Render chart-specific options in the modal form
  renderOptions: ({
    Form,
    formState,
    setters,
    validationErrors,
    hasSubmitted,
    getGroupingColumns,
    getValueColumns,
    getAggregationOptions,
    getColumnDisplayName,
    getAggregationLabel,
    municipality
  }) => {
    const { groupBy, valueColumn, aggregationMethod } = formState
    const { setGroupBy, setValueColumn, setAggregationMethod, setValidationErrors } = setters

    return (
      <div className="mb-3">
        <GroupByOption
          Form={Form}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getGroupingColumns={getGroupingColumns}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
          label="Group By (Slice Categories)"
        />

        <ValueColumnOption
          Form={Form}
          valueColumn={valueColumn}
          setValueColumn={setValueColumn}
          aggregationMethod={aggregationMethod}
          setAggregationMethod={setAggregationMethod}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getValueColumns={getValueColumns}
          getAggregationOptions={getAggregationOptions}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
          label="Slice Column (What Each Slice Represents)"
          allowNone={true}
        />

        <AggregationMethodOption
          Form={Form}
          aggregationMethod={aggregationMethod}
          setAggregationMethod={setAggregationMethod}
          valueColumn={valueColumn}
          getAggregationOptions={getAggregationOptions}
          getAggregationLabel={getAggregationLabel}
          label="Slice Aggregation"
        />
      </div>
    )
  }
}

export default React.memo(PieDonutChart)
